import { useState } from 'react';
import { ArrowUpRight, CircleCheck } from 'lucide-react';
import type { EmrProgress } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/primitives';

const RING_RADIUS = 15;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

export function ProgressRing({ done, total, className }: { done: number; total: number; className?: string }) {
  const share = total > 0 ? done / total : 1;
  const complete = done >= total;
  return (
    <svg viewBox="0 0 36 36" className={cn('h-8 w-8 shrink-0 -rotate-90', className)} aria-hidden>
      <circle cx="18" cy="18" r={RING_RADIUS} fill="none" strokeWidth="4" className="stroke-muted" />
      <circle
        cx="18"
        cy="18"
        r={RING_RADIUS}
        fill="none"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - share)}
        className={cn('transition-[stroke-dashoffset] duration-500', complete ? 'stroke-success' : 'stroke-warning')}
      />
    </svg>
  );
}

export function MissingFormsList({ missing, onOpen }: { missing: readonly string[]; onOpen: (key: string) => void }) {
  return (
    <ul className="space-y-1">
      {missing.map((key) => (
        <li key={key}>
          <button
            type="button"
            onClick={() => onOpen(key)}
            className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start text-sm transition-colors hover:bg-muted focus-ring"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{tr('opd', `emr_form_${key}`)}</span>
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 rtl:-scale-x-100" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function EmrProgressIndicator({ progress, onOpen }: { progress: EmrProgress; onOpen: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  if (progress.required === 0) return null;
  const complete = progress.missing.length === 0;
  const summary = tr('opd', 'emr_required_x_of_y', { done: progress.done, total: progress.required });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={summary}
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-lg border bg-card px-2 pe-3 text-start transition-colors hover:bg-muted focus-ring',
            complete ? 'border-success/30' : 'border-warning/40',
          )}
        >
          <ProgressRing done={progress.done} total={progress.required} className="h-6 w-6" />
          <span className="leading-tight">
            <span className="block text-xs font-semibold tabular">{summary}</span>
            <span className={cn('hidden text-[10px] sm:block', complete ? 'text-success' : 'text-muted-foreground')}>
              {complete ? tr('opd', 'emr_ready_to_close') : tr('opd', 'emr_required_to_close')}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        {complete ? (
          <p className="flex items-center gap-2 text-sm text-success">
            <CircleCheck className="h-4 w-4 shrink-0" aria-hidden /> {tr('opd', 'emr_all_required_done')}
          </p>
        ) : (
          <div className="space-y-2">
            <div>
              <p className="text-sm font-semibold">{tr('opd', 'emr_still_needed')}</p>
              <p className="text-xs text-muted-foreground">{tr('opd', 'emr_still_needed_hint')}</p>
            </div>
            <MissingFormsList
              missing={progress.missing}
              onOpen={(key) => {
                setOpen(false);
                onOpen(key);
              }}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
